/*
  7Raw Carnet Noir runtime (sections/7r-carnet-index.liquid, sections/7r-carnet-entry.liquid).

  <r7-carnet-index>: pointer over a row, or keyboard focus on it, makes the row active: the other rows dim to
  .35, its name column shifts 12px (published rows only) and the sticky panel cross-fades to its image and
  caption. Leaving the list (pointer or focus) returns to the idle row (data-default-index). Touch input is
  ignored, so a tap follows the row link.

  <r7-carnet-entry>:
  - types the H1 name in at 60ms per character with an "_" caret. The full name stays in the H1's
    aria-label and returns as plain text when the typing ends; the untyped rest keeps its space;
  - reveals each paragraph that starts below the fold when it scrolls into view (IntersectionObserver;
    opacity 1s, translateY 18px → 0 over 1.1s; see 7r-carnet.css);
  - scales the 2px reading-progress bar with the page scroll. The bar is moved to <body> so no ancestor
    stacking context (the page-in fade of <main>) can put it under the sticky header.
  Reduced motion (only when R7.prefersReducedMotion(), i.e. the theme setting is on and the device asks):
  no typing and no reveal. The progress bar stays.

  Both elements tear their listeners down with an AbortController. The theme editor replaces a section's
  DOM on shopify:section:load, which disconnects the old element and connects the new one, so they
  re-initialise on their own.
*/
(() => {
  const reducedMotion = () => Boolean(window.R7?.prefersReducedMotion?.());

  if (!customElements.get('r7-carnet-index')) {
    customElements.define(
      'r7-carnet-index',
      class R7CarnetIndex extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-carnet-list]');
          if (!this.list) return;
          this.rows = Array.from(this.querySelectorAll('[data-carnet-row]'));
          this.slides = Array.from(this.querySelectorAll('[data-carnet-slide]'));
          this.captions = Array.from(this.querySelectorAll('[data-carnet-caption]'));
          this.idle = Number(this.dataset.defaultIndex) || 0;
          this.shown = this.idle;

          this.controller = new AbortController();
          const { signal } = this.controller;

          this.rows.forEach((row) => {
            row.addEventListener(
              'pointerenter',
              (event) => {
                if (event.pointerType !== 'touch') this.activate(row);
              },
              { signal }
            );
          });

          this.list.addEventListener(
            'pointerleave',
            (event) => {
              if (event.pointerType !== 'touch') this.activate(null);
            },
            { signal }
          );

          this.list.addEventListener(
            'focusin',
            (event) => {
              const row = event.target.closest('[data-carnet-row]');
              if (row) this.activate(row);
            },
            { signal }
          );

          this.list.addEventListener(
            'focusout',
            (event) => {
              if (!this.list.contains(event.relatedTarget)) this.activate(null);
            },
            { signal }
          );
        }

        disconnectedCallback() {
          this.controller?.abort();
        }

        activate(row) {
          const index = row ? Number(row.dataset.index) : null;

          this.list.toggleAttribute('data-hovering', index !== null);
          this.rows.forEach((item) => item.classList.toggle('is-hovered', item === row));

          const shown = index !== null ? index : this.idle;
          if (shown === this.shown) return;
          this.shown = shown;

          this.slides.forEach((slide) => {
            slide.classList.toggle('is-shown', Number(slide.dataset.index) === shown);
          });
          this.captions.forEach((caption) => {
            caption.hidden = Number(caption.dataset.index) !== shown;
          });
        }
      }
    );
  }

  if (!customElements.get('r7-carnet-entry')) {
    customElements.define(
      'r7-carnet-entry',
      class R7CarnetEntry extends HTMLElement {
        static TYPE_MS = 60;
        static REVEAL_LIMIT = 0.88;

        connectedCallback() {
          this.controller = new AbortController();
          const { signal } = this.controller;
          const reduced = reducedMotion();

          this.setupProgress();
          this.setupTyping(reduced);
          this.setupReveal(reduced);

          if (this.bar || this.pending?.length) {
            const onScroll = () => this.requestUpdate();
            window.addEventListener('scroll', onScroll, { passive: true, signal });
            window.addEventListener('resize', onScroll, { passive: true, signal });
            this.update();
          }
        }

        disconnectedCallback() {
          this.controller?.abort();
          clearInterval(this.typeTimer);
          cancelAnimationFrame(this.frame);
          this.frame = null;
          this.observer?.disconnect();
          this.bar?.remove();
          if (this.nameEl && this.fullName) this.nameEl.textContent = this.fullName;
        }

        /* Reading progress */

        setupProgress() {
          this.bar = this.bar || this.querySelector('[data-carnet-progress]');
          if (this.bar && this.bar.parentElement !== document.body) document.body.append(this.bar);
        }

        requestUpdate() {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        }

        update() {
          if (this.bar) {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const ratio = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
            this.bar.style.transform = `scaleX(${ratio.toFixed(4)})`;
          }

          /* A fast jump can carry a paragraph past the viewport without it ever intersecting. */
          if (this.pending?.length) {
            this.pending
              .filter((para) => para.getBoundingClientRect().bottom < 0)
              .forEach((para) => this.reveal(para));
          }
        }

        /* Typing */

        setupTyping(reduced) {
          const title = this.querySelector('[data-carnet-title]');
          const nameEl = title?.querySelector('[data-carnet-name]');
          if (!nameEl) return;

          const full = nameEl.textContent.trim();
          const chars = Array.from(full);
          if (reduced || chars.length === 0) {
            title.classList.add('is-typed');
            return;
          }

          this.nameEl = nameEl;
          this.fullName = full;

          const typed = document.createElement('span');
          const rest = document.createElement('span');
          typed.setAttribute('aria-hidden', 'true');
          rest.setAttribute('aria-hidden', 'true');
          rest.className = 'r7-carnet-entry__rest';
          nameEl.replaceChildren(typed, rest);
          title.classList.remove('is-typed');
          title.classList.add('is-typing');

          let count = 0;
          const render = () => {
            const typing = count < chars.length;
            const caret = typing ? (chars[count] === ' ' ? ' ' : '_') : '';
            typed.textContent = chars.slice(0, count).join('') + caret;
            rest.textContent = typing ? chars.slice(count + 1).join('') : '';
          };

          render();
          this.typeTimer = setInterval(() => {
            count += 1;
            if (count < chars.length) {
              render();
              return;
            }
            clearInterval(this.typeTimer);
            nameEl.textContent = full;
            title.classList.replace('is-typing', 'is-typed');
          }, R7CarnetEntry.TYPE_MS);
        }

        /* Paragraph reveal */

        setupReveal(reduced) {
          const paras = Array.from(this.querySelectorAll('[data-carnet-para]'));
          this.pending = [];
          if (reduced || paras.length === 0 || !('IntersectionObserver' in window)) return;

          const limit = window.innerHeight * R7CarnetEntry.REVEAL_LIMIT;
          this.pending = paras.filter((para) => para.getBoundingClientRect().top >= limit);
          if (this.pending.length === 0) return;

          this.pending.forEach((para) => para.classList.add('is-armed'));
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.reveal(entry.target);
              });
            },
            { rootMargin: `0px 0px -${Math.round((1 - R7CarnetEntry.REVEAL_LIMIT) * 100)}% 0px` }
          );
          this.pending.forEach((para) => this.observer.observe(para));
        }

        reveal(para) {
          this.observer?.unobserve(para);
          this.pending = this.pending.filter((item) => item !== para);
          para.classList.add('is-revealing');
          para.classList.remove('is-armed');
        }
      }
    );
  }
})();
